import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styles from './Ludo_game.module.css';

/* ============================================================================
 * LUDO GAME — single-page React component (Ludo King style)
 * ----------------------------------------------------------------------------
 * - 2-player (You vs 1 ) and 4-player (You vs 3 ) modes
 * - Classic 15x15 board with coloured start arrows, safe stars, home lanes
 * - Smooth cell-by-cell token movement (tokens hop through every square)
 * - Capture, safe-cells, extra-turn-on-six, three-sixes-forfeit rules
 * - postGameEvent() / requestBotDecision() are isolated so this file can be
 *   wired to a real backend later without touching the rest of the logic.
 * - Game state is persisted to localStorage so an in-progress match survives
 *   a page refresh / accidental tab close.
 * ==========================================================================*/

/* ---------------------------------------------------------------------- */
/* Board geometry (constants — computed once, outside the component)      */
/* ---------------------------------------------------------------------- */

// The 52 shared track cells, clockwise, starting at Red's entry cell.
const PATH = [
  [6, 1], [6, 2], [6, 3], [6, 4], [6, 5],
  [5, 6], [4, 6], [3, 6], [2, 6], [1, 6], [0, 6],
  [0, 7],
  [0, 8], [1, 8], [2, 8], [3, 8], [4, 8], [5, 8],
  [6, 9], [6, 10], [6, 11], [6, 12], [6, 13], [6, 14],
  [7, 14],
  [8, 14], [8, 13], [8, 12], [8, 11], [8, 10], [8, 9],
  [9, 8], [10, 8], [11, 8], [12, 8], [13, 8], [14, 8],
  [14, 7],
  [14, 6], [13, 6], [12, 6], [11, 6], [10, 6], [9, 6],
  [8, 5], [8, 4], [8, 3], [8, 2], [8, 1], [8, 0],
  [7, 0],
  [6, 0],
];

const COLORS = ['red', 'green', 'yellow', 'blue'];

const START_INDEX = { red: 0, green: 13, yellow: 26, blue: 39 };

// Start cell + direction of travel (for the directional arrows).
const START_CELL = {
  '6,1': { color: 'red', dir: 'Right' },
  '1,8': { color: 'green', dir: 'Down' },
  '8,13': { color: 'yellow', dir: 'Left' },
  '13,6': { color: 'blue', dir: 'Up' },
};

// Extra directional arrows placed along the outer track so the direction of
// travel is obvious all the way around the board, not just at the 4 start
// cells. Each entry's direction is the verified direction of travel THROUGH
// that exact cell (derived directly from the PATH array — the cell the arrow
// sits on is the same cell whose direction it describes, unlike the corner
// "tip" cells such as [0,7]/[7,14]/[14,7]/[7,0], which are mid-turn cells
// with a diagonal transition either side and are intentionally skipped).
const PATH_ARROWS = {
  '6,3': 'Right',  // red's outbound straight (PATH idx 2)
  '3,6': 'Up',     // top-left arm, vertical run (idx 7)
  '0,6': 'Right',  // top bridge cell, curving into the next arm (idx 10)
  '3,8': 'Down',   // top-right arm, vertical run (idx 15)
  '6,11': 'Right', // right arm, horizontal run (idx 20)
  '6,14': 'Down',  // right bridge cell (idx 23)
  '8,11': 'Left',  // bottom-right arm, horizontal run (idx 28)
  '11,8': 'Down',  // bottom-right arm, vertical run (idx 33)
  '14,8': 'Left',  // bottom bridge cell (idx 36)
  '11,6': 'Up',    // bottom-left arm, vertical run (idx 41)
  '8,3': 'Left',   // bottom-left arm, horizontal run (idx 46)
  '8,0': 'Up',     // left bridge cell (idx 49)
};

// Degrees to rotate the base "point right" arrow glyph for each direction.
const ARROW_ROTATION = { Right: 0, Down: 90, Left: 180, Up: 270 };

// Neutral slate color for the in-path direction markers — these mark shared
// track cells that every color's tokens pass through, so they intentionally
// don't use a player color (only the 4 start cells are player-colored).
const TRACK_ARROW_COLOR = '#94a3b8';

const HOME_LANE = {
  red: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5]],
  green: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]],
  yellow: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]],
  blue: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7]],
};

const BASE_RECT = {
  red: { r: 0, c: 0 },
  green: { r: 0, c: 9 },
  yellow: { r: 9, c: 9 },
  blue: { r: 9, c: 0 },
};

const BASE_SLOT_OFFSETS = [[1.35, 1.35], [1.35, 3.65], [3.65, 1.35], [3.65, 3.65]];

const SAFE_GLOBAL = new Set([0, 8, 13, 21, 26, 34, 39, 47]);

const DICE_PATTERNS = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const PLAYER_META = {
  red: { label: 'Red', avatar: '🔴' },
  green: { label: 'Green', avatar: '🟢' },
  yellow: { label: 'Yellow', avatar: '🟡' },
  blue: { label: 'Blue', avatar: '🔵' },
};

// Milliseconds per single-cell hop during a move animation.
const STEP_MS = 175;

//  pacing — longer delays make the bots feel more deliberate/human.
const BOT_ROLL_DELAY = 1150; // pause before a bot picks up the dice
const BOT_MOVE_DELAY = 820;  // "thinking" pause before a bot commits a move

// Each player's dice sits in the centre of their home base (percent of board).
const DICE_POS = {
  red: { top: 8, left: 25 },
  green: { top: 8, left: 75 },
  yellow: { top: 92, left: 75 },
  blue: { top: 92, left: 25 },
};

// localStorage key + schema version. Bump the version if the shape of the
// persisted payload ever changes, so old saves are ignored instead of
// crashing the app.
const STORAGE_KEY = 'ludoArena.saveV1';

const cap = (s) => s[0].toUpperCase() + s.slice(1);

/** Board [row, col] for a token's relative position (-1 = base, 0-50 = common
 *  track, 51-55 = home lane, 56 = finished / centre). */
function coordForRel(color, rel) {
  if (rel <= -1) return null;
  if (rel <= 50) return PATH[(START_INDEX[color] + rel) % 52];
  if (rel <= 55) return HOME_LANE[color][rel - 51];
  return [7, 7];
}

function isSafeRel(color, rel) {
  if (rel < 0) return true;
  if (rel > 50) return true; // home lane / finished
  return SAFE_GLOBAL.has((START_INDEX[color] + rel) % 52);
}

/* ---------------------------------------------------------------------- */
/* Static board cell map (built once)                                     */
/* ---------------------------------------------------------------------- */

function buildBoardCells() {
  const pathKeys = new Map();
  PATH.forEach(([r, c], idx) => pathKeys.set(`${r},${c}`, idx));

  const homeKeys = new Map();
  COLORS.forEach((color) => {
    HOME_LANE[color].forEach(([r, c]) => homeKeys.set(`${r},${c}`, color));
  });

  const wedge = {
    '6,7': 'green',
    '8,7': 'blue',
    '7,6': 'red',
    '7,8': 'yellow',
  };

  const cells = [];
  for (let r = 0; r < 15; r++) {
    for (let c = 0; c < 15; c++) {
      const key = `${r},${c}`;

      const baseColor = COLORS.find((color) => {
        const b = BASE_RECT[color];
        return r >= b.r && r < b.r + 6 && c >= b.c && c < b.c + 6;
      });

      if (baseColor) {
        const b = BASE_RECT[baseColor];
        if (r === b.r && c === b.c) {
          cells.push({ key, type: 'base', color: baseColor, r, c, span: 6 });
        }
        continue;
      }

      if (r === 7 && c === 7) {
        cells.push({ key, type: 'center', r, c });
      } else if (START_CELL[key]) {
        cells.push({ key, type: 'start', color: START_CELL[key].color, dir: START_CELL[key].dir, r, c });
      } else if (pathKeys.has(key)) {
        const globalIdx = pathKeys.get(key);
        cells.push({
          key,
          type: SAFE_GLOBAL.has(globalIdx) ? 'safe' : 'path',
          r,
          c,
          arrowDir: PATH_ARROWS[key] || null,
        });
      } else if (homeKeys.has(key)) {
        cells.push({ key, type: 'home', color: homeKeys.get(key), r, c });
      } else if (wedge[key]) {
        cells.push({ key, type: 'wedge', color: wedge[key], r, c });
      } else {
        cells.push({ key, type: 'blank', r, c });
      }
    }
  }
  return cells;
}

const BOARD_CELLS = buildBoardCells();

/* ---------------------------------------------------------------------- */
/* Persistence helpers                                                    */
/* ---------------------------------------------------------------------- */

function loadSavedGame() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.screen !== 'game' || !Array.isArray(parsed.players) || parsed.players.length === 0) {
      return null;
    }
    return parsed;
  } catch (err) {
    // Corrupt or incompatible save — ignore it and start fresh.
    return null;
  }
}

function persistGame(snapshot) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (err) {
    // Storage full / unavailable (e.g. private browsing) — fail silently,
    // the game still works, it just won't survive a refresh.
  }
}

function clearSavedGame() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    /* ignore */
  }
}

/* ---------------------------------------------------------------------- */
/* Backend-ready hooks                                                    */
/* ---------------------------------------------------------------------- */

function postGameEvent(event) {
  // TODO: send over WebSocket / REST to a real backend.
  if (typeof window !== 'undefined' && window.__LUDO_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('[ludo:event]', event);
  }
}

async function requestBotDecision(moves, player) {
  // TODO: replace with a server call to move  decisions server-side.
  return chooseBotMove(moves, player);
}

/* ---------------------------------------------------------------------- */
/* Game logic helpers                                                     */
/* ---------------------------------------------------------------------- */

function makePlayers(mode) {
  if (mode === '2p') {
    return [
      { color: 'red', isHuman: true, name: 'You', tokens: [-1, -1, -1, -1], finished: 0 },
      { color: 'yellow', isHuman: false, name: ' Yellow', tokens: [-1, -1, -1, -1], finished: 0 },
    ];
  }
  return [
    { color: 'red', isHuman: true, name: 'You', tokens: [-1, -1, -1, -1], finished: 0 },
    { color: 'green', isHuman: false, name: ' Green', tokens: [-1, -1, -1, -1], finished: 0 },
    { color: 'yellow', isHuman: false, name: ' Yellow', tokens: [-1, -1, -1, -1], finished: 0 },
    { color: 'blue', isHuman: false, name: ' Blue', tokens: [-1, -1, -1, -1], finished: 0 },
  ];
}

function computeCaptures(mover, newRel, players) {
  if (newRel > 50) return []; // home lane / finished cells are always safe
  const global = (START_INDEX[mover.color] + newRel) % 52;
  if (SAFE_GLOBAL.has(global)) return [];

  const captures = [];
  players.forEach((p, pIdx) => {
    if (p.color === mover.color) return;
    p.tokens.forEach((rel, tIdx) => {
      if (rel < 0 || rel > 50) return;
      const g = (START_INDEX[p.color] + rel) % 52;
      if (g === global) captures.push({ playerIndex: pIdx, tokenIndex: tIdx });
    });
  });
  return captures;
}

function getValidMoves(player, diceValue, players) {
  const moves = [];
  player.tokens.forEach((rel, tokenIndex) => {
    if (rel === -1) {
      if (diceValue === 6) {
        const captures = computeCaptures(player, 0, players);
        moves.push({ tokenIndex, newRel: 0, captures });
      }
      return;
    }
    if (rel >= 56) return; // already finished
    const newRel = rel + diceValue;
    if (newRel > 56) return; // overshoot — illegal, must roll exact
    const captures = computeCaptures(player, newRel, players);
    moves.push({ tokenIndex, newRel, captures });
  });
  return moves;
}

function chooseBotMove(moves, player) {
  const capture = moves
    .filter((m) => m.captures.length > 0)
    .sort((a, b) => b.captures.length - a.captures.length)[0];
  if (capture) return capture;

  const finishing = moves.find((m) => m.newRel === 56);
  if (finishing) return finishing;

  const activeOnBoard = player.tokens.filter((t) => t >= 0 && t < 56).length;
  const exiting = moves.find((m) => m.tokenIndex >= 0 && player.tokens[m.tokenIndex] === -1);
  if (exiting && activeOnBoard < 2) return exiting;

  const safeMove = moves
    .filter((m) => isSafeRel(player.color, m.newRel))
    .sort((a, b) => b.newRel - a.newRel)[0];
  if (safeMove) return safeMove;

  return [...moves].sort((a, b) => b.newRel - a.newRel)[0];
}

/* ---------------------------------------------------------------------- */
/* Small presentational pieces                                            */
/* ---------------------------------------------------------------------- */

function Dice({ value, rolling, corner }) {
  const pattern = DICE_PATTERNS[value] || [4];
  return (
    <div
      className={[styles.dice, corner ? styles.cornerDice : '', rolling ? styles.diceRolling : ''].join(' ')}
      aria-label={`Dice showing ${value}`}
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className={`${styles.diceDot} ${pattern.includes(i) ? styles.diceDotOn : ''}`} />
      ))}
    </div>
  );
}

// Self-contained directional arrow used for the in-path track markers (no
// dependency on external CSS — it's an inline SVG chevron rotated to point
// the right way). `color` is a literal CSS color (e.g. '#94a3b8'), not a
// CSS-variable name.
function DirectionArrow({ color, dir, size = '42%' }) {
  const rotation = ARROW_ROTATION[dir] ?? 0;
  return (
    <svg
      viewBox="0 0 24 24"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: size,
        height: size,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        pointerEvents: 'none',
        opacity: 0.75,
      }}
    >
      <polygon points="4,3 21,12 4,21 9,12" fill={color} stroke="#ffffff" strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}

function Token({ color, top, left, movable, finished, moving, onClick, title }) {
  return (
    <div
      className={[
        styles.token,
        styles[`token${cap(color)}`],
        movable ? styles.tokenMovable : '',
        moving ? styles.tokenMoving : '',
        finished ? styles.tokenFinished : '',
      ].join(' ')}
      style={{ top: `${top}%`, left: `${left}%` }}
      onClick={movable ? onClick : undefined}
      title={title}
    >
      <svg className={styles.pawn} viewBox="0 0 40 52" xmlns="http://www.w3.org/2000/svg">
        <ellipse className={styles.pawnShadow} cx="20" cy="47.5" rx="12.5" ry="3.8" />
        <path
          className={styles.pawnBody}
          d="M20 3.5c-4.4 0-8 3.6-8 8 0 2.9 1.55 5.45 3.85 6.85C11.4 21.5 8.6 27.5 8 34.5h24c-.6-7-3.4-13-7.85-16.15C26.45 16.95 28 14.4 28 11.5c0-4.4-3.6-8-8-8z"
        />
        <path
          className={styles.pawnBase}
          d="M7.5 34.5h25c1.7 0 3 1.55 3 3.5s-1.3 3.5-3 3.5h-25c-1.7 0-3-1.55-3-3.5s1.3-3.5 3-3.5z"
        />
        <ellipse className={styles.pawnGloss} cx="16" cy="11.5" rx="2.4" ry="3.4" />
      </svg>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Main component                                                         */
/* ---------------------------------------------------------------------- */

export default function LudoGame() {
  // Try to resume an in-progress match on first render.
  const initialSave = typeof window !== 'undefined' ? loadSavedGame() : null;

  const [screen, setScreen] = useState(initialSave ? 'game' : 'menu'); // 'menu' | 'game'
  const [mode, setMode] = useState(initialSave ? initialSave.mode : null);

  const [players, setPlayers] = useState(initialSave ? initialSave.players : []);
  const [turn, setTurn] = useState(initialSave ? initialSave.turn : 0);
  const [dice, setDice] = useState(initialSave ? initialSave.dice : 1);
  const [playerDice, setPlayerDice] = useState(initialSave ? initialSave.playerDice : [1, 1, 1, 1]); // last settled value per player
  const [rolling, setRolling] = useState(false);
  const [moving, setMoving] = useState(false);
  const [animToken, setAnimToken] = useState(null); // { pIdx, tIdx, rel } while stepping
  const [movableTokens, setMovableTokens] = useState(initialSave ? initialSave.movableTokens || [] : []);
  const [awaitingHuman, setAwaitingHuman] = useState(initialSave ? !!initialSave.awaitingHuman : false);
  const [canRoll, setCanRoll] = useState(initialSave ? !!initialSave.canRoll : true);
  const [sixStreak, setSixStreak] = useState(initialSave ? initialSave.sixStreak || 0 : 0);
  const [log, setLog] = useState(initialSave ? initialSave.log || [] : []);
  const [winner, setWinner] = useState(() => {
    if (!initialSave || initialSave.winnerColor == null) return null;
    return (initialSave.players || []).find((p) => p.color === initialSave.winnerColor) || null;
  });
  const [status, setStatus] = useState(initialSave ? initialSave.status || 'Welcome back!' : 'Roll to begin!');

  // Mirror of the latest state so timer callbacks never see stale closures.
  const ref = useRef({});
  useEffect(() => {
    ref.current = { players, turn, dice, rolling, moving, movableTokens, awaitingHuman, canRoll, sixStreak, winner };
  }, [players, turn, dice, rolling, moving, movableTokens, awaitingHuman, canRoll, sixStreak, winner]);

  const stepTimer = useRef(null);
  useEffect(() => () => clearTimeout(stepTimer.current), []);

  const addLog = useCallback((message, highlight = false) => {
    setLog((prev) => [...prev.slice(-49), { message, highlight, id: Math.random().toString(36).slice(2) }]);
  }, []);

  /* --------------------------- persistence ---------------------------- */

  // Save whenever the game reaches a "stable" point — i.e. not mid-animation
  // (moving) and not mid dice-roll spin (rolling). This avoids ever saving a
  // transient in-between frame that would look broken on reload.
  useEffect(() => {
    if (screen !== 'game' || moving || rolling) return;
    persistGame({
      screen,
      mode,
      players,
      turn,
      dice,
      playerDice,
      movableTokens,
      awaitingHuman,
      canRoll,
      sixStreak,
      log,
      status,
      winnerColor: winner ? winner.color : null,
    });
  }, [screen, mode, players, turn, dice, playerDice, movableTokens, awaitingHuman, canRoll, sixStreak, log, status, winner, moving, rolling]);

  /* ------------------------------ setup ------------------------------ */

  const startGame = (selectedMode) => {
    const initialPlayers = makePlayers(selectedMode);
    setMode(selectedMode);
    setPlayers(initialPlayers);
    setTurn(0);
    setDice(1);
    setPlayerDice(initialPlayers.map(() => 1));
    setRolling(false);
    setMoving(false);
    setAnimToken(null);
    setMovableTokens([]);
    setAwaitingHuman(false);
    setCanRoll(true);
    setSixStreak(0);
    setWinner(null);
    setLog([]);
    setStatus('Your roll!');
    setScreen('game');
    postGameEvent({ type: 'game_start', mode: selectedMode, players: initialPlayers.map((p) => p.color) });
  };

  const backToMenu = () => {
    clearTimeout(stepTimer.current);
    clearSavedGame();
    setScreen('menu');
    setMode(null);
  };

  /* ------------------------------ turns ------------------------------ */

  const goToNextTurn = useCallback(() => {
    const g = ref.current;
    const nextTurn = (g.turn + 1) % g.players.length;
    setTurn(nextTurn);
    setCanRoll(true);
    setMovableTokens([]);
    setAwaitingHuman(false);
    setSixStreak(0);
    setStatus(`${g.players[nextTurn].isHuman ? 'Your' : g.players[nextTurn].name + "'s"} turn`);
  }, []);

  // Runs AFTER the movement animation finishes: commits the token, applies
  // captures, logs, and hands off the turn.
  const finalizeMove = useCallback((playerIndex, tokenIndex, fromRel, newRel, diceValue, streak) => {
    setPlayers((prevPlayers) => {
      const players2 = prevPlayers.map((p) => ({ ...p, tokens: [...p.tokens] }));
      const mover = players2[playerIndex];
      mover.tokens[tokenIndex] = newRel;

      const captures = computeCaptures(mover, newRel, players2);
      captures.forEach(({ playerIndex: pIdx, tokenIndex: tIdx }) => {
        players2[pIdx].tokens[tIdx] = -1;
      });

      const justFinished = newRel === 56;
      if (justFinished) mover.finished += 1;

      if (captures.length > 0) {
        captures.forEach(({ playerIndex: pIdx }) => {
          addLog(`${mover.name} sent ${players2[pIdx].name}'s piece home! 💥`, true);
        });
      }
      if (justFinished) {
        addLog(`${mover.name} got a piece all the way home! 🏆`, true);
      } else if (fromRel === -1) {
        addLog(`${mover.name} brought a new piece into play.`);
      } else {
        addLog(`${mover.name} moved a piece ${diceValue} steps.`);
      }

      postGameEvent({ type: 'move', color: mover.color, tokenIndex, from: fromRel, to: newRel, captures });

      const wonGame = mover.finished === 4;
      const earnsExtraTurn = diceValue === 6 || captures.length > 0 || justFinished;
      const moverName = mover.name;
      const moverRef = mover;

      // Let the captured pieces slide home before switching turns.
      setTimeout(() => {
        setMoving(false);
        if (wonGame) {
          setWinner(moverRef);
          setStatus(`${moverName} wins! 🎉`);
          postGameEvent({ type: 'game_over', winner: moverRef.color });
          clearSavedGame();
          return;
        }
        if (earnsExtraTurn) {
          setTurn(playerIndex);
          setCanRoll(true);
          setMovableTokens([]);
          setAwaitingHuman(false);
          setSixStreak(streak);
          setStatus(diceValue === 6 ? 'Rolled a 6 — go again!' : 'Extra turn — go again!');
        } else {
          goToNextTurn();
        }
      }, captures.length ? 340 : 40);

      return players2;
    });
  }, [addLog, goToNextTurn]);

  // Steps a token one cell at a time from fromRel to newRel, then finalizes.
  const performMove = useCallback((playerIndex, tokenIndex, diceValue, streak) => {
    const g = ref.current;
    const fromRel = g.players[playerIndex].tokens[tokenIndex];
    const newRel = fromRel === -1 ? 0 : fromRel + diceValue;

    setMoving(true);
    setAwaitingHuman(false);
    setMovableTokens([]);

    const steps = [];
    if (fromRel === -1) {
      steps.push(0); // pop out of base onto the start cell
    } else {
      for (let r = fromRel + 1; r <= newRel; r++) steps.push(r);
    }

    let i = 0;
    const advance = () => {
      setAnimToken({ pIdx: playerIndex, tIdx: tokenIndex, rel: steps[i] });
      i += 1;
      if (i < steps.length) {
        stepTimer.current = setTimeout(advance, STEP_MS);
      } else {
        stepTimer.current = setTimeout(() => {
          setAnimToken(null);
          finalizeMove(playerIndex, tokenIndex, fromRel, newRel, diceValue, streak);
        }, STEP_MS);
      }
    };
    advance();
  }, [finalizeMove]);

  /* ------------------------------ dice ------------------------------- */

  const proceedAfterRoll = useCallback((finalValue, streak) => {
    const g = ref.current;
    const player = g.players[g.turn];

    if (finalValue === 6 && streak === 3) {
      addLog(`${player.name} rolled three 6s in a row — turn forfeited.`, true);
      setTimeout(goToNextTurn, 650);
      return;
    }

    const moves = getValidMoves(player, finalValue, g.players);

    if (moves.length === 0) {
      addLog(`${player.name} rolled a ${finalValue} — no valid moves.`);
      setTimeout(() => {
        if (finalValue === 6) {
          setCanRoll(true);
          setStatus('Rolled a 6 but no moves — roll again!');
          setSixStreak(streak);
        } else {
          goToNextTurn();
        }
      }, 650);
      return;
    }

    if (player.isHuman) {
      if (moves.length === 1) {
        setStatus('Moving your only available piece…');
        setTimeout(() => performMove(g.turn, moves[0].tokenIndex, finalValue, streak), 350);
      } else {
        setMovableTokens(moves.map((m) => m.tokenIndex));
        setAwaitingHuman(true);
        setStatus('Tap a glowing piece to move it');
      }
    } else {
      setStatus(`${player.name} is thinking…`);
      requestBotDecision(moves, player).then((choice) => {
        setTimeout(() => performMove(g.turn, choice.tokenIndex, finalValue, streak), BOT_MOVE_DELAY);
      });
    }
  }, [addLog, goToNextTurn, performMove]);

  const rollDice = useCallback(() => {
    const g = ref.current;
    if (!g.canRoll || g.rolling || g.moving || g.awaitingHuman || g.winner) return;

    setRolling(true);
    setCanRoll(false);
    setAwaitingHuman(false);
    setMovableTokens([]);

    let ticks = 0;
    const spin = setInterval(() => {
      setDice(1 + Math.floor(Math.random() * 6));
      ticks += 1;
      if (ticks >= 7) {
        clearInterval(spin);
        const finalValue = 1 + Math.floor(Math.random() * 6);
        setDice(finalValue);
        setPlayerDice((prev) => {
          const next = [...prev];
          next[ref.current.turn] = finalValue;
          return next;
        });
        setRolling(false);
        const streak = finalValue === 6 ? ref.current.sixStreak + 1 : 0;
        setSixStreak(streak);
        postGameEvent({ type: 'dice_roll', color: ref.current.players[ref.current.turn].color, value: finalValue });
        proceedAfterRoll(finalValue, streak);
      }
    }, 70);
  }, [proceedAfterRoll]);

  const handleTokenClick = (tokenIndex) => {
    const g = ref.current;
    if (g.moving || !g.awaitingHuman || !g.movableTokens.includes(tokenIndex)) return;
    performMove(g.turn, tokenIndex, g.dice, g.sixStreak);
  };

  // Auto-roll for bots.
  useEffect(() => {
    if (screen !== 'game' || winner) return undefined;
    const current = players[turn];
    if (current && !current.isHuman && canRoll && !rolling && !moving && !awaitingHuman) {
      const t = setTimeout(() => rollDice(), BOT_ROLL_DELAY);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [screen, winner, players, turn, canRoll, rolling, moving, awaitingHuman, rollDice]);

  /* ------------------------------ render ------------------------------ */

  const currentPlayer = players[turn];

  // Value shown on a player's corner dice (live spin for the active roller).
  const cornerValue = (i) => (i === turn && rolling ? dice : (playerDice[i] ?? 1));

  const tokenNodes = useMemo(() => {
    if (screen !== 'game' || players.length === 0) return [];
    const nodes = [];
    const occupancy = {};

    players.forEach((player, pIdx) => {
      player.tokens.forEach((committedRel, tIdx) => {
        const isAnim = animToken && animToken.pIdx === pIdx && animToken.tIdx === tIdx;
        const rel = isAnim ? animToken.rel : committedRel;

        let row, col;
        if (rel === -1) {
          const [or, oc] = BASE_SLOT_OFFSETS[tIdx];
          const b = BASE_RECT[player.color];
          row = b.r + or;
          col = b.c + oc;
        } else {
          const coord = coordForRel(player.color, Math.min(rel, 56));
          row = coord[0];
          col = coord[1];
          const key = `${row},${col}`;
          const n = occupancy[key] || 0;
          occupancy[key] = n + 1;
          if (n > 0) {
            const dx = [0, 0.28, -0.28, 0.28][n % 4];
            const dy = [0, -0.28, 0.28, 0.28][n % 4];
            row += dy;
            col += dx;
          }
        }

        const movable = awaitingHuman && pIdx === turn && movableTokens.includes(tIdx);
        nodes.push(
          <Token
            key={`${player.color}-${tIdx}`}
            color={player.color}
            top={((row + 0.5) / 15) * 100}
            left={((col + 0.5) / 15) * 100}
            movable={movable}
            moving={isAnim}
            finished={rel === 56}
            onClick={() => handleTokenClick(tIdx)}
            title={`${player.name} · piece ${tIdx + 1}`}
          />
        );
      });
    });
    return nodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, screen, awaitingHuman, turn, movableTokens, animToken]);

  /* -------------------------- menu screen -------------------------- */

  if (screen === 'menu') {
    return (
      <div className={styles.app}>
        <div className={styles.modeScreen}>
          <div className={styles.kicker}>Arcade Board Game</div>
          <h1 className={styles.title}>LUDO ARENA</h1>
          <p className={styles.subtitle}>
            Roll the dice, race your pieces home, and take down your rivals.
          </p>

          <div className={styles.modeGrid}>
            <button type="button" className={styles.modeCard} onClick={() => startGame('2p')}>
              <span className={styles.modeCardIcon}>🎯</span>
              <h3 className={styles.modeCardTitle}>2 Player</h3>
              <p className={styles.modeCardDesc}>You vs. 1 . Quick head-to-head match on a classic board.</p>
              <div className={styles.modeCardDots}>
                <span className={styles.dotChip} style={{ background: '#ea3b3b' }} />
                <span className={styles.dotChip} style={{ background: '#f4c21b' }} />
              </div>
            </button>

            <button type="button" className={styles.modeCard} onClick={() => startGame('4p')}>
              <span className={styles.modeCardIcon}>👥</span>
              <h3 className={styles.modeCardTitle}>4 Player</h3>
              <p className={styles.modeCardDesc}>You vs. 3  . The full classic Ludo chaos, every corner in play.</p>
              <div className={styles.modeCardDots}>
                <span className={styles.dotChip} style={{ background: '#ea3b3b' }} />
                <span className={styles.dotChip} style={{ background: '#2ca94f' }} />
                <span className={styles.dotChip} style={{ background: '#f4c21b' }} />
                <span className={styles.dotChip} style={{ background: '#2e7de0' }} />
              </div>
            </button>
          </div>

          <div className={styles.footNote}>
            <span className={styles.pulseDot} />
            Players  are online and ready to play
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------- game screen -------------------------- */

  return (
    <div className={styles.app}>
      <div className={styles.gameScreen}>
        <div className={styles.topBar}>
          <div className={styles.logo}>🎲 LUDO <span>ARENA</span></div>
          <div className={styles.topBarRight}>
            <span className={styles.pill}>{mode === '2p' ? '2 Player' : '4 Player'} mode</span>
            <button type="button" className={styles.ghostBtn} onClick={backToMenu}>New Game</button>
          </div>
        </div>

        <div className={styles.card}>
          <h4 className={styles.cardTitle}>Players</h4>
          <div className={styles.playersList}>
            {players.map((p, idx) => (
              <div key={p.color} className={`${styles.playerRow} ${idx === turn ? styles.playerRowActive : ''}`}>
                <span className={styles.playerColorDot} style={{ background: `var(--${p.color})`, color: `var(--${p.color})` }} />
                <div className={styles.playerInfo}>
                  <span className={styles.playerName}>{p.name}</span>
                  <span className={styles.playerTag}>{p.isHuman ? 'Human' : ' '} · {PLAYER_META[p.color].label}</span>
                </div>
                <span className={styles.playerProgress}>{p.finished}/4</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.mainLayout}>
          <div className={styles.boardWrap}>
            <div className={styles.board}>
              {BOARD_CELLS.map((cell) => {
                if (cell.type === 'base') {
                  return (
                    <div
                      key={cell.key}
                      className={`${styles.baseZone} ${styles[`baseZone${cap(cell.color)}`]}`}
                      style={{ gridColumn: `${cell.c + 1} / span ${cell.span}`, gridRow: `${cell.r + 1} / span ${cell.span}` }}
                    >
                      <div className={styles.baseInner} />
                      {BASE_SLOT_OFFSETS.map(([or, oc], i) => (
                        <span
                          key={i}
                          className={styles.baseSlot}
                          style={{
                            top: `${((or + 0.5) / 6) * 100}%`,
                            left: `${((oc + 0.5) / 6) * 100}%`,
                            width: '26%',
                            height: '26%',
                          }}
                        />
                      ))}
                    </div>
                  );
                }
                if (cell.type === 'center') {
                  return (
                    <div key={cell.key} className={styles.cellCenter} style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1 }}>
                      <span className={`${styles.homeTri} ${styles.homeTriTop}`} />
                      <span className={`${styles.homeTri} ${styles.homeTriRight}`} />
                      <span className={`${styles.homeTri} ${styles.homeTriBottom}`} />
                      <span className={`${styles.homeTri} ${styles.homeTriLeft}`} />
                    </div>
                  );
                }
                if (cell.type === 'start') {
                  return (
                    <div
                      key={cell.key}
                      className={`${styles.cell} ${styles.cellStart} ${styles[`cellStart${cap(cell.color)}`]}`}
                      style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1 }}
                    >
                      <span className={`${styles.startArrow} ${styles[`arrow${cell.dir}`]}`} />
                    </div>
                  );
                }
                if (cell.type === 'home') {
                  const cls = styles[`cellHome${cap(cell.color)}`];
                  return <div key={cell.key} className={`${styles.cell} ${cls}`} style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1 }} />;
                }
                if (cell.type === 'wedge') {
                  const cls = styles[`wedge${cap(cell.color)}`];
                  return <div key={cell.key} className={`${styles.cell} ${cls}`} style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1 }} />;
                }
                if (cell.type === 'safe') {
                  return (
                    <div
                      key={cell.key}
                      className={`${styles.cell} ${styles.cellPath} ${styles.cellSafe}`}
                      style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1, position: 'relative' }}
                    >
                      {cell.arrowDir && <DirectionArrow color={TRACK_ARROW_COLOR} dir={cell.arrowDir} size="42%" />}
                    </div>
                  );
                }
                if (cell.type === 'path') {
                  return (
                    <div
                      key={cell.key}
                      className={`${styles.cell} ${styles.cellPath}`}
                      style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1, position: 'relative' }}
                    >
                      {cell.arrowDir && <DirectionArrow color={TRACK_ARROW_COLOR} dir={cell.arrowDir} size="42%" />}
                    </div>
                  );
                }
                return <div key={cell.key} className={styles.cell} style={{ gridColumn: cell.c + 1, gridRow: cell.r + 1 }} />;
              })}
            
              {tokenNodes}
            </div>
              {players.map((p, i) => {
                const pos = DICE_POS[p.color];
                const isTurn = i === turn && !winner;
                const clickable = isTurn && p.isHuman && canRoll && !rolling && !moving && !awaitingHuman;
                
                return (
                  <div
                    key={`dice-${p.color}`}
                    className={[
                      styles.diceSlot,
                      isTurn ? styles.diceSlotActive : '',
                      clickable ? styles.diceSlotClickable : '',
                    ].join(' ')}
                    style={{ top: `${pos.top}%`, left: `${pos.left}%`,position:'absolute',zIndex:'100', ['--dice-accent']: `var(--${p.color})` }}
                    onClick={clickable ? rollDice : undefined}
                    title={p.name}
                  >
                    <div key={p.color} className={`${styles.playerRow} ${i === turn ? styles.playerRowActive : ''}`}>
                       <Dice corner value={cornerValue(i)} rolling={isTurn && rolling} />
                {/* <span className={styles.playerColorDot} style={{ background: `var(--${p.color})`, color: `var(--${p.color})` }} /> */}
                <div className={styles.playerInfo}>
                  <span className={styles.playerName}>{p.name}</span>
                  <span className={styles.playerTag}>{p.isHuman ? 'Human' : ' '} · {PLAYER_META[p.color].label}</span>
                </div>
                <span className={styles.playerProgress}>{p.finished}/4</span>
              </div>
                   
                  </div>
                );
              })}
          </div>

          <div className={styles.sidePanel}>
            <div className={`${styles.card} ${styles.turnCard}`}>
              <div className={styles.turnLabel}>Now Playing</div>
              {currentPlayer && (
                <div className={styles.turnName} style={{ color: `var(--${currentPlayer.color})` }}>
                  <span
                    className={styles.turnAvatar}
                    style={{ background: `var(--${currentPlayer.color})`, color: `var(--${currentPlayer.color})` }}
                  />
                  {currentPlayer.name}
                </div>
              )}

              <div className={styles.diceZone}>
                <button
                  type="button"
                  className={styles.rollBtn}
                  disabled={!canRoll || rolling || moving || !currentPlayer?.isHuman || !!winner}
                  onClick={rollDice}
                >
                  {rolling ? 'Rolling…' : 'Roll Dice'}
                </button>
                <div className={`${styles.statusText} ${awaitingHuman ? styles.statusTextActive : ''}`}>{status}</div>
              </div>
            </div>

            <div className={styles.card}>
              <h4 className={styles.cardTitle}>Match Log</h4>
              <div className={styles.logPanel}>
                {[...log].reverse().map((entry) => (
                  <div key={entry.id} className={`${styles.logItem} ${entry.highlight ? styles.logItemHighlight : ''}`}>
                    {entry.message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {winner && (
        <div className={styles.winnerOverlay}>
          <div className={styles.winnerModal}>
            <div className={styles.winnerBadge}>{winner.isHuman ? '🏆' : '🤖'}</div>
            <h2 className={styles.winnerTitle}>{winner.name} wins!</h2>
            <p className={styles.winnerSub}>
              {winner.isHuman ? 'Great race — all 4 pieces made it home.' : 'Better luck next time, the  played sharp.'}
            </p>
            <button type="button" className={styles.playAgainBtn} onClick={() => startGame(mode)}>
              Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}